<template>
  <div class="w-full max-w-4xl pb-12">
    <form class="flex flex-col max-w-xl pt-4 meeting-about-form md_pt-12" @submit.prevent="submit">
      <h3 class="text-gray-900">Tell us about yourself</h3>

      <label class="meeting-about-form-label" for="name">
        Full Name
        <VInput
          v-model="name"
          v-bind="nameAttrs"
          type="text"
          name="name"
          :error="!!errors.name"
          :maxlength="120"
          data-testid="book-a-meeting-name-input"
        />
        <div v-if="errors.name" class="error">{{ errors.name }}</div>
      </label>

      <label class="meeting-about-form-label" for="email">
        Email
        <VInput
          v-bind="emailAttrs"
          v-model="email"
          type="email"
          name="email"
          :maxlength="120"
          :error="!!errors.email"
          :readonly="user && !!user.email_address"
          data-testid="book-a-meeting-email-input"
        />
        <div v-if="errors.email" class="error">{{ errors.email }}</div>
      </label>

      <label class="meeting-about-form-label" for="guests">Add Guest Email(s)</label>

      <VChipInput name="guests" type="email" :value="guests" @chip-input-error="chipInputError = $event" @chip-added="guests = $event" />

      <label class="mt-10 mb-1 text-sm text-gray-800" for="describe">What type of investor best describes you?</label>

      <VDropdown
        v-model="investorType"
        class="text-gray-800"
        size="sm"
        placeholder="Select an investor type"
        :options="investorTypeOptions"
        data-testid="book-a-meeting-investor-type-input"
      >
        <template #current-single="{ item }">{{ item.name }}</template>
        <template #option="{ item }">
          <div class="py-1 pl-2 pr-2 text-sm">
            {{ item.name }}
          </div>
        </template>
      </VDropdown>

      <label class="meeting-about-form-label" for="talk-about">What would you like to talk about?</label>
      <VInput
        v-model="about"
        type="textarea"
        name="talk-about"
        placeholder="Recent press release, drill results, quarter results, etc."
        data-testid="book-a-meeting-about-input"
        :maxlength="255"
        rows="3"
      />

      <div class="flex gap-2 mt-14">
        <VButton @click="$emit('back')">Back</VButton>
        <VButton type="submit" data-testid="book-a-meeting-submit" variant="dark" :disabled="!name || !email || chipInputError"
          >Book this Meeting</VButton
        >
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { z } from 'zod'
import type { DateTime } from 'luxon'
import type { Ref, PropType, DeepReadonly } from 'vue'
import ZodMessages from '@/zod_presets/messages'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'

import type { User } from '@/entities/user'
import type { Company } from '@/entities/company'
import type { ScheduleSetting } from '@/entities/meeting'
import { INVESTOR_TYPE_OPTIONS } from '@/lib/constants/meetings'

const props = defineProps({
  company: {
    type: Object as PropType<Company>,
    required: true,
  },
  meetingSettings: {
    type: Object as PropType<DeepReadonly<ScheduleSetting>>,
    required: true,
  },
  meetingDateTime: {
    type: Object as PropType<DateTime>,
    required: true,
  },
  meetingDuration: {
    type: Number,
    required: true,
  },
  user: {
    type: Object as PropType<DeepReadonly<User>>,
    default: () => ({}),
  },
})

const emit = defineEmits(['back', 'submit'])

const route = useRoute()
const { $analytics, $sentry } = useNuxtApp()
const { showAlert } = useAlerts()

const AboutYouFormSchema = z.object({
  name: z.string().nonempty(ZodMessages.name).default(''),
  email: z
    .string()
    .email(ZodMessages.email)
    .default(props.user?.email_address || ''),
})
const { handleSubmit, errors, defineField } = useForm<z.infer<typeof AboutYouFormSchema>>({
  validationSchema: toTypedSchema(AboutYouFormSchema),
})
const [name, nameAttrs] = defineField('name')
const [email, emailAttrs] = defineField('email')

const guests = ref([])
const about = ref('')
const investorType = ref(INVESTOR_TYPE_OPTIONS[0])
const chipInputError: Ref<boolean> = ref(false)
const investorTypeOptions = INVESTOR_TYPE_OPTIONS

const submit = handleSubmit(async () => {
  if (chipInputError.value) return

  try {
    await $fetch('v2/meetings/requests', {
      method: 'POST',
      body: {
        name: name.value,
        email_address: email.value,
        investor_description: investorType.value.id,
        meeting_description: about.value,
        participant_email_addresses: guests.value,
        scheduled_start_at: props.meetingDateTime.toISO(),
        scheduled_end_at: props.meetingDateTime.plus({ minutes: props.meetingDuration }).toISO(),
        timezone: props.meetingDateTime.zoneName,
        request_setting_id: props.meetingSettings.id,
      },
      ...useFetchSharedOptions(),
    })

    $analytics.track('BAMInvestorClickBookThisMeeting', {
      request_setting_id: route.params.id,
      companyId: props.company.id,
      companyName: props.company.name,
    })
    emit('submit')
  } catch (e) {
    showAlert({
      alertType: 'danger',
      alertMessage: 'Whoops! Something went wrong. Please try again.',
      alertTimeout: 5000,
      shouldPersistThroughNav: false,
    })
    $sentry?.captureException(e)
  }
})
</script>

<style lang="stylus" scoped>
.meeting-about-form
  :deep(.select-box)
    @apply border-gray-400

    &.active
      .options-container
        @apply border-gray-400

.meeting-about-form-label
  @apply text-sm text-gray-800 mb-1 mt-4

.error
  @apply text-sm text-red-800 mt-2
</style>
